
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { AlertCircle, FileEdit } from "lucide-react";
import { format, parseISO } from "date-fns";
import { DraftCampaign } from "@/hooks/campaign/hooks/useCampaignInit";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useLocalDraftList } from '@/hooks/useLocalDraftList';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DraftsListProps {
  drafts: DraftCampaign[];
  loading: boolean;
  onDraftDeleted?: () => void;
}

export const DraftsList = ({ drafts, loading, onDraftDeleted }: DraftsListProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { deleteDraft } = useLocalDraftList();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Debug logging
  useEffect(() => {
    console.log('DraftsList: Received drafts prop:', drafts);
    console.log('DraftsList: Loading state:', loading);
    console.log('DraftsList: Drafts count:', drafts?.length || 0);
  }, [drafts, loading]);

  const handleConfirmDelete = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(id);
  };

  const handleDeleteDraft = async () => {
    if (!confirmDeleteId) return;
    
    const id = confirmDeleteId;
    setDeletingId(id);
    
    try {
      await deleteDraft(id);
      
      toast({
        title: t('draftDeleted'),
        description: t('draftDeletedSuccess'),
      });
      
      // Trigger the parent component's refresh function
      if (onDraftDeleted) {
        onDraftDeleted();
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast({
        title: t('errorDeletingDraft'),
        description: t('problemDeletingDraft'),
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  // Helper function to safely parse dates
  const formatDate = (dateString: string) => {
    try {
      // Try to parse as ISO string first
      const date = parseISO(dateString);
      if (isNaN(date.getTime())) {
        // If that fails, try parsing as regular Date
        const fallbackDate = new Date(dateString);
        if (isNaN(fallbackDate.getTime())) {
          return 'Unknown date';
        }
        return format(fallbackDate, 'PPp');
      }
      return format(date, 'PPp');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown date';
    }
  };

  if (loading) {
    console.log('DraftsList: Showing loading state');
    return (
      <div className="text-center py-2">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
        <p className="text-sm mt-2">{t('loadingDrafts')}</p>
      </div>
    );
  }

  if (!drafts || drafts.length === 0) {
    console.log('DraftsList: No drafts found, showing empty state');
    return (
      <div className="flex items-center justify-center py-4 text-center text-muted-foreground">
        <div>
          <AlertCircle className="h-5 w-5 mx-auto mb-2 opacity-70" />
          <p className="text-sm">{t('noDraftsFound')}</p>
        </div>
      </div>
    );
  }

  console.log('DraftsList: Rendering drafts list with', drafts.length, 'drafts');
  
  return (
    <>
      <div className="space-y-3">
        {drafts.slice(0, 3).map((draft) => (
          <div 
            key={draft.id}
            className="block p-3 rounded-md bg-background/80 hover:bg-background transition-colors border border-border hover:border-primary/30"
          >
            <Link
              to={`/campaign/draft/${draft.id}`}
              className="flex justify-between items-center"
            >
              <div>
                <p className="font-medium">{draft.campaign_name || 'Untitled Campaign'}</p>
                <p className="text-xs text-muted-foreground">
                  {t('lastEdited')}: {formatDate(draft.updated_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <FileEdit className="h-4 w-4 text-muted-foreground" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-full hover:bg-destructive/10 hover:text-destructive"
                  onClick={(e) => handleConfirmDelete(draft.id, e)}
                  disabled={deletingId === draft.id}
                >
                  {deletingId === draft.id ? 
                    <div className="h-3 w-3 rounded-full border-2 border-b-transparent animate-spin"></div> : 
                    <Trash2 className="h-3 w-3" />
                  }
                </Button>
              </div>
            </Link>
          </div>
        ))}

        {drafts.length > 3 && (
          <Link 
            to="/drafts" 
            className="text-xs text-primary hover:underline block text-center mt-1"
          >
            {t('viewAllDrafts')} ({drafts.length})
          </Link>
        )}
      </div>

      <AlertDialog open={!!confirmDeleteId} onOpenChange={() => setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirmDeleteDraft')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('confirmDeleteDraft')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteDraft}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
